import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { extendTrialFrom } from "@atsready/shared";
import type { UserRole } from "@atsready/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { extendTrial } from "@/services/subscriptions";
import type { AdminUserDetail } from "@/services/types";
import {
  EXTEND_TRIAL_DEFAULT_DAYS,
  EXTEND_TRIAL_INVALID_DAYS_NOTE,
  EXTEND_TRIAL_MAX_DAYS,
  EXTEND_TRIAL_MIN_DAYS,
  EXTEND_TRIAL_PRESETS,
  describeExtendTrial,
  describeExtendTrialAction,
  describeExtendTrialHorizon,
  formatTrialDate,
  parseExtendTrialDays,
  resolveExtendTrialGate,
  resolveExtendTrialHorizon,
} from "@/lib/extend-trial";
import type { ExtendTrialBlock, ExtendableSubscription } from "@/lib/extend-trial";

/**
 * `M13` — the control that replaces the `+7 Days Trial` button.
 *
 * The button it replaces POSTed a path no admin route declared, so it had never
 * worked and had never said so: the failure surfaced as a `destructive` toast
 * reading *"Failed to extend trial"*, which reads as a server hiccup (D-001).
 * It also sat *above* the `canModify` gate that wraps Disable / Enable, so a
 * SUPPORT or an ANALYST could fire it.
 *
 * **This panel refuses first, in words.** Five account-level conditions would
 * each draw a `403`, a `404` or a `400` from the route, and for every one of
 * them the form, the presets, the number, the reason and the commit button are
 * **not rendered at all** — there is no disabled control to hover, only `M13`'s
 * stated-reason block saying what is true and what to do. That matters most
 * during the fortnight the gifted trial is live: every actively-paying
 * subscriber is `TRIALING` *with* a Stripe subscription for those two weeks, so
 * without the Stripe branch an ADMIN would meet the old toast on the largest
 * population in the base rather than the smallest.
 *
 * The sixth refusal — the horizon — is deliberately the other shape. It is a
 * property of the typed **number**, not of the account, so hiding the form
 * would hide it from an operator whose next keystroke would make the request
 * legal. The form stays, the commit disables, and an inline note names the
 * maximum.
 *
 * Two deviations from the fragment, both forced by the package rather than
 * chosen. `M13`'s token block sets `--primary: #2a85ff`, and `admin`'s real
 * `--primary` is the template orange with no `ThemeProvider` to substitute for
 * it, so this is built against the package's own tokens and hard-codes no hue.
 * `M13` also declares `--radius: 0.625rem`; `admin` hard-codes its radii per
 * component, so the controls follow their neighbours instead.
 *
 * What the panel does **not** prove is that the route exists. Mocking
 * `extendTrial` and asserting it was called is precisely the test that stayed
 * green for the whole life of a button that never worked; the server half is
 * the route's own test and the walk on a deployed environment.
 */

interface ExtendTrialPanelProps {
  /** The user whose detail query is invalidated on success. */
  userId: string | undefined;
  /** The row as the GET projection serves it, `C4`'s two new fields included. */
  subscription: AdminUserDetail["subscription"];
  /** The signed-in admin's role, or `null` when there is no session user. */
  role: UserRole | null;
  /** Whether the signed-in admin is looking at their own account. */
  isSelf: boolean;
}

/** What the server said, kept so the panel can render it instead of its preview. */
interface CommittedExtension {
  days: number;
  previousTrialEndDate: string;
  newTrialEndDate: string;
  countedFromToday: boolean;
}

function LockedReason({ block, role }: { block: ExtendTrialBlock; role: UserRole | null }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-4">
      <h4 className="mb-2 text-base font-bold">Extend trial</h4>
      {lockedCopy(block, role).map((sentence) => (
        <p key={sentence} className="mt-1.5 text-sm text-muted-foreground">
          {sentence}
        </p>
      ))}
    </div>
  );
}

/**
 * One shape for "you cannot do this, and here is why", five times over.
 *
 * `M13` draws exactly one treatment for a stated refusal, and reusing it for
 * all five means an operator learns to read one block rather than five. The
 * copy is a table rather than a switch because four of the five are constant;
 * only `below-admin` names the role the reader is actually signed in as.
 */
const STATIC_LOCKED_COPY: Record<Exclude<ExtendTrialBlock, "below-admin">, string[]> = {
  "no-subscription": [
    "This account has no subscription record.",
    "There is no trial here to extend, and this control does not create one.",
  ],
  "self-action": [
    "This is your own account. Extending your own trial is the one use of this control with no operational reading, so it is refused — ask another ADMIN.",
  ],
  "stripe-managed": [
    "This trial is managed by Stripe, so its dates belong to Stripe rather than to us. Extending it here would be overwritten the next time Stripe reports the subscription.",
    "Change the trial end on the subscription in the Stripe dashboard instead.",
  ],
  "no-trial": [
    "There is no trial on this account to extend.",
    "This control extends a trial; it does not start one.",
  ],
};

function lockedCopy(block: ExtendTrialBlock, role: UserRole | null): string[] {
  if (block === "below-admin") {
    return [
      `Extending a trial changes what this person is paying for, so it needs the ADMIN role. ${
        role === null ? "This session carries no admin role." : `You are signed in as ${role}.`
      }`,
      "Ask an ADMIN or a SUPER_ADMIN to make the change. Everything above is yours to read.",
    ];
  }
  return STATIC_LOCKED_COPY[block];
}

function ExtendTrialForm({
  userId,
  subscription,
}: {
  userId: string | undefined;
  subscription: ExtendableSubscription;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [daysRaw, setDaysRaw] = useState(String(EXTEND_TRIAL_DEFAULT_DAYS));
  const [reason, setReason] = useState("");
  const [committed, setCommitted] = useState<CommittedExtension | null>(null);

  const mutation = useMutation({
    mutationFn: (variables: { days: number; reason: string }) =>
      extendTrial(subscription.id, variables.days, variables.reason),
    onSuccess: (response, variables) => {
      setCommitted({
        days: variables.days,
        previousTrialEndDate: response.previousTrialEndDate,
        newTrialEndDate: response.newTrialEndDate,
        countedFromToday: response.countedFromToday,
      });
      queryClient.invalidateQueries({ queryKey: ["admin-user", userId] });
      toast({ title: "Trial extended successfully" });
    },
    onError: (error) => {
      toast({
        title: "Failed to extend trial",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const [now] = useState(() => new Date());
  const trialEnd = new Date(subscription.trialEndDate);
  const days = parseExtendTrialDays(daysRaw);
  const preview =
    days === null
      ? null
      : {
          days,
          ...extendTrialFrom(trialEnd, days, now),
          horizon: resolveExtendTrialHorizon(subscription, days, now),
        };

  const canCommit =
    preview !== null &&
    !preview.horizon.exceeded &&
    reason.trim().length > 0 &&
    !mutation.isPending;

  const commit =
    preview === null
      ? undefined
      : () => {
          mutation.mutate({ days: preview.days, reason: reason.trim() });
        };

  return (
    <div className="space-y-4">
      <h4 className="text-base font-bold">Extend trial</h4>

      <fieldset className="min-w-0 border-0 p-0">
        <legend className="text-sm font-semibold">Add</legend>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <div className="flex" role="group" aria-label="Preset lengths">
            {EXTEND_TRIAL_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                size="sm"
                variant="outline"
                aria-pressed={days === preset}
                className="mr-1"
                onClick={() => {
                  setDaysRaw(String(preset));
                }}
              >
                {preset}
              </Button>
            ))}
          </div>
          <span className="text-sm text-muted-foreground">or</span>
          <Label htmlFor="extend-trial-days" className="text-muted-foreground">
            days
          </Label>
          <Input
            id="extend-trial-days"
            type="number"
            inputMode="numeric"
            min={EXTEND_TRIAL_MIN_DAYS}
            max={EXTEND_TRIAL_MAX_DAYS}
            step={1}
            className="w-24 tabular-nums"
            value={daysRaw}
            aria-describedby="extend-trial-days-help"
            onChange={(event) => {
              setDaysRaw(event.target.value);
            }}
          />
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground" id="extend-trial-days-help">
          1 to 90 days. The cap is what stops a mistyped 365 from handing away a year of Pro+.
        </p>
      </fieldset>

      <div>
        <Label htmlFor="extend-trial-reason" className="mb-1.5 block">
          Reason
        </Label>
        <Input
          id="extend-trial-reason"
          type="text"
          placeholder="e.g. import failed twice during onboarding"
          value={reason}
          aria-describedby="extend-trial-reason-help"
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        <p className="mt-1.5 text-xs text-muted-foreground" id="extend-trial-reason-help">
          Written to the audit log beside your name. Required — “Admin action” tells the next person
          nothing.
        </p>
      </div>

      <div className="grid gap-0.5 rounded-2xl border border-border bg-muted/40 px-3.5 py-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          After extending
        </span>
        <span className="text-lg font-bold tabular-nums">
          {preview === null ? "—" : formatTrialDate(preview.newTrialEndDate)}
        </span>
        <span className="text-xs text-muted-foreground">
          {preview === null
            ? EXTEND_TRIAL_INVALID_DAYS_NOTE
            : describeExtendTrial(
                trialEnd,
                preview.newTrialEndDate,
                preview.days,
                preview.countedFromToday,
                now,
              )}
        </span>
      </div>

      {preview !== null && preview.horizon.exceeded && (
        <p className="text-xs text-destructive" role="alert">
          {describeExtendTrialHorizon(preview.horizon.maxDays)}
        </p>
      )}

      <Button type="button" size="sm" disabled={!canCommit} onClick={commit}>
        {describeExtendTrialAction(days)}
      </Button>

      {committed !== null && (
        <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm" role="status">
          Trial extended to {formatTrialDate(new Date(committed.newTrialEndDate))}.{" "}
          {describeExtendTrial(
            new Date(committed.previousTrialEndDate),
            new Date(committed.newTrialEndDate),
            committed.days,
            committed.countedFromToday,
            now,
          )}
        </p>
      )}
    </div>
  );
}

export function ExtendTrialPanel({ userId, subscription, role, isSelf }: ExtendTrialPanelProps) {
  const gate = resolveExtendTrialGate(subscription, role, isSelf);

  if (gate.block !== null) {
    return <LockedReason block={gate.block} role={role} />;
  }

  return <ExtendTrialForm userId={userId} subscription={gate.subscription} />;
}
